"use client"

import { useState, useCallback, useMemo, useRef, useEffect } from "react"
import { getFootmanConfig } from "@/data/barracks-config"
import type { GameState, GameStats, Position, ParticleEffect, LandedPod, EntanglingRootsEffect, FireGroundEffect, Corpse, AoeVisualEffect, PendingAoeWave, LaserBeamEffect, AbductionAnimationState, SoldierSpawnPortrait, ItemDropMessage } from "@/types/game.types"
import type { Enemy } from "@/types/enemy.types"
import type { Tower, TowerType } from "@/types/tower.types"
import type { Hero, HeroType, ActiveSkillEffect } from "@/types/hero.types"
import type { Projectile } from "@/types/projectile.types"
import type { DropPodRewards } from "@/types/drop-pod.types"
import type { SummonedUnit } from "@/types/summon.types"
import type { BarracksSoldier } from "@/types/barracks.types"
import type { HeroInventory, ActiveItemEffect, ItemEffectType, DroppedItem } from "@/types/item.types"
import { type WaveSpawner, createWaveSpawner, startWave } from "@/systems/wave-system"
import { createHero, healHero, setHeroDestination } from "@/systems/hero-system"
import { createStartingInventory, createEmptyInventory, useItem as consumeInventoryItem, canUseItem, getItemDefinition, removeItemFromInventory, isCloneBeacon, addItemToInventory, createDroppedItem } from "@/systems/item-system"
import { activateSkillshot, activateDash, activateTargetedDash, activateTargetedProjectile, activateSummon, activateAOE, activateEntanglingRoots, activateBuff, canUseSkill, getSkillByHotkey, upgradeSkill, upgradeSkillWithGold, getInterestBonus, getSkillGoldCost, getSkillUpgradeManaCost, getSkillLevelStats } from "@/systems/skill-system"
import { setSummonDestination } from "@/systems/summon-system"
import { distanceSquared } from "@/utils/collision"
import { createTower, canPlaceTower, getTowerCost, upgradeTower } from "@/systems/tower-system"
import { levels, getLevelByIndex, getTotalLevels } from "@/data/levels"
import { constants, TILE_SIZE, ENEMY_PROGRESSION_MAX } from "@/data/game-settings"
import { generateId } from "@/utils/id-generator"
import { gameSettings } from "@/data/game-settings"
import { heroTypes } from "@/data/hero-types"
import { resolveStartingHero } from "@/lib/player-prefs"
import { discover } from "@/lib/codex-discovery"
import { towerTypes } from "@/data/tower-types"
import { clearAllClones } from "@/services/hero-clone-service"
import type { MapState, MapNode } from "@/types/map.types"
import { generateMap, advanceToNode, completeCurrentNode } from "@/systems/map-generation"
import { getShrineSkillUpgradeCost } from "@/systems/shrine-system"
import type { UpgradeState } from "@/types/upgrade.types"
import { initialUpgradeState } from "@/types/upgrade.types"
import { unlockUpgrade as unlockUpgradeFromSystem } from "@/systems/upgrade-system"
import { playLevelCompleteSound, playNewWaveSound, playSiegesmithForgeTurretSound, playTowerBuildSound, playPotionSound, playPaladinVanguardRushTravelSound, playPaladinSpectralBladeSound, playRangerEntangledRootsSound, playSkillLevelUpSound, playSellSound, playRangerWildkinCallSound, playArmorEquipSound, playArmorDropSound, playSiegesmithTremorSlamSound } from "@/hooks/use-sound-effects"
import type { Player, PlayerId, MultiplayerState } from "@/types/player.types"
import { gameNow } from "@/utils/game-clock"

// Helper function to calculate tower score based on total investment
// Baseline: Level 1 Archer Tower (50 gold) = 1 point
const calculateTowerScore = (towers: Tower[]): number => {
  let totalInvestment = 0
  for (const tower of towers) {
    const config = towerTypes[tower.type]
    if (!config) continue
    // Sum all upgrade costs up to current level
    for (let i = 0; i < tower.level; i++) {
      totalInvestment += config.upgrades[i].cost
    }
  }
  // Baseline: 50 gold (L1 archer) = 1 point
  return totalInvestment / 50
}

// Helper function to get particle colors based on item effect type
// Returns combined colors for items with multiple effects (e.g., heal + mana)
const getItemEffectColors = (effectTypes: ItemEffectType[]): string[] => {
  const colors: string[] = []

  // Add colors for each effect type present
  if (effectTypes.includes("heal")) {
    colors.push("#22c55e", "#4ade80") // Green for healing
  }
  if (effectTypes.includes("mana")) {
    colors.push("#3b82f6", "#60a5fa") // Blue for mana
  }
  if (effectTypes.includes("absorb")) {
    colors.push("#6366f1", "#818cf8") // Indigo/purple for shields
  }
  if (effectTypes.includes("teleport")) {
    colors.push("#00CED1", "#40E0D0") // Cyan for teleport
  }
  if (effectTypes.includes("lure")) {
    colors.push("#DAA520", "#FFD700") // Golden for lure
  }
  if (effectTypes.includes("damage-boost")) {
    colors.push("#ef4444", "#f87171") // Red for damage
  }
  if (effectTypes.includes("speed-boost")) {
    colors.push("#f59e0b", "#fbbf24") // Orange for speed
  }
  if (effectTypes.includes("armor") || effectTypes.includes("max-health")) {
    colors.push("#9ca3af", "#d1d5db") // Silver/gray for armor/health
  }
  if (effectTypes.includes("lifesteal")) {
    colors.push("#8B0000", "#DC143C") // Dark red for lifesteal
  }
  if (effectTypes.includes("spell-power")) {
    colors.push("#a855f7", "#c084fc") // Purple for spell power
  }

  // Default to gold/amber if no specific effect colors
  if (colors.length === 0) {
    return ["#fbbf24", "#fcd34d", "#fde68a", "#fef3c7"]
  }

  return colors
}

// ============================================================================
// MULTIPLAYER HELPERS
// ============================================================================

/**
 * Create a new player instance
 */
const createPlayer = (id: PlayerId, name: string, hero: Hero | null, inventory: HeroInventory, upgradeState: UpgradeState): Player => {
  return {
    id,
    name,
    isLocal: true, // For now, all players are local (single-player mode)
    hero,
    inventory,
    upgradeState,
    stats: {
      kills: 0,
      damageDealt: 0,
    },
  }
}

export const useGameState = () => {
  const [currentLevelIndex, setCurrentLevelIndex] = useState(0)
  const [currentWaveIndex, setCurrentWaveIndex] = useState(0)
  const level = getLevelByIndex(currentLevelIndex) || levels[0]

  const [gameState, setGameState] = useState<GameState>("title")
  const [stats, setStats] = useState<GameStats>({
    money: level.startMoney,
    lives: level.startLives,
    wave: 1,
    score: 0,
  })

  const [enemies, setEnemies] = useState<Enemy[]>([])
  const [towers, setTowers] = useState<Tower[]>([])
  const [hero, setHero] = useState<Hero | null>(null)
  const [projectiles, setProjectiles] = useState<Projectile[]>([])
  const [selectedTowerId, setSelectedTowerId] = useState<string | null>(null)
  const [selectedHeroId, setSelectedHeroId] = useState<string | null>(null)
  const [selectedEnemyId, setSelectedEnemyId] = useState<string | null>(null)
  const [showNextWaveButton, setShowNextWaveButton] = useState(false)
  const [waveStartTime, setWaveStartTime] = useState<number | null>(null) // Timestamp when current wave started
  const [interestAnimation, setInterestAnimation] = useState<number | null>(null)
  const [sellAnimation, setSellAnimation] = useState<number | null>(null)
  const [particleEffects, setParticleEffects] = useState<ParticleEffect[]>([])
  const [freePremiumTowers, setFreePremiumTowers] = useState(0)
  const [isScreenShaking, setIsScreenShaking] = useState(false)
  const [screenShakeOffset, setScreenShakeOffset] = useState({ x: 0, y: 0 })
  const screenShakeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [landedPod, setLandedPod] = useState<LandedPod | null>(null)
  const [skillEffects, setSkillEffects] = useState<ActiveSkillEffect[]>([])
  const [summonedUnits, setSummonedUnits] = useState<SummonedUnit[]>([])
  const [selectedSummonId, setSelectedSummonId] = useState<string | null>(null)
  const [pendingSkillId, setPendingSkillId] = useState<string | null>(null) // Skill waiting for target location
  const [pendingSkillExecution, setPendingSkillExecution] = useState<{
    skillId: string
    targetPosition: Position
    targetEnemyId?: string
    requiredRange: number
  } | null>(null) // Skill queued to execute when hero reaches range
  const [rootsEffects, setRootsEffects] = useState<EntanglingRootsEffect[]>([]) // Entangling roots effects
  const [fireEffects, setFireEffects] = useState<FireGroundEffect[]>([]) // Fire ground effects from siege towers
  const [heroInventory, setHeroInventory] = useState<HeroInventory>(createEmptyInventory())
  const [activeItemEffects, setActiveItemEffects] = useState<ActiveItemEffect[]>([]) // Active item effects (HoTs, shields, lures)
  const [pendingItemSlot, setPendingItemSlot] = useState<number | null>(null) // Item slot waiting for target (teleport, lure)
  const [corpses, setCorpses] = useState<Corpse[]>([]) // Enemy corpses
  const [aoeVisualEffects, setAoeVisualEffects] = useState<AoeVisualEffect[]>([]) // Visual effects for AoE abilities
  const [pendingAoeWaves, setPendingAoeWaves] = useState<PendingAoeWave[]>([]) // Pending AOE wave damage (for multi-wave abilities)
  const [lastUpgradedSkillId, setLastUpgradedSkillId] = useState<string | null>(null) // Track last upgraded skill for animation
  const [droppedItems, setDroppedItems] = useState<DroppedItem[]>([]) // Items dropped on the ground
  const [laserBeamEffects, setLaserBeamEffects] = useState<LaserBeamEffect[]>([]) // Laser beams from forge turrets
  const [cloneRestoreEffect, setCloneRestoreEffect] = useState<{ active: boolean; restoredLevel: number | null }>({ active: false, restoredLevel: null }) // Clone restoration effect
  const [barracksSoldiers, setBarracksSoldiers] = useState<BarracksSoldier[]>([]) // Soldiers spawned by barracks towers
  const [soldierSpawnPortraits, setSoldierSpawnPortraits] = useState<SoldierSpawnPortrait[]>([]) // Floating portraits when soldiers spawn
  const [itemDropMessages, setItemDropMessages] = useState<ItemDropMessage[]>([]) // Floating messages when items drop from enemies
  const [selectedSoldierId, setSelectedSoldierId] = useState<string | null>(null) // Currently selected barracks soldier
  const [isSettingRallyPoint, setIsSettingRallyPoint] = useState(false) // True when player is placing a rally point
  const [pendingRallyBarracksId, setPendingRallyBarracksId] = useState<string | null>(null) // Barracks tower waiting for rally point placement
  const [isLastWaveComplete, setIsLastWaveComplete] = useState(false) // True when last wave complete, waiting for player to enter shop
  const [abductionAnimation, setAbductionAnimation] = useState<AbductionAnimationState | null>(null)
  const scoreAtLevelStartRef = useRef<number>(0)
  const [heroismLevel, setHeroismLevel] = useState<number>(0) // Global heroism level (0-7), resets on new level

  // Map state for branching node navigation (Slay the Spire style)
  const [mapState, setMapState] = useState<MapState | null>(null)
  const [isMapOpen, setIsMapOpen] = useState(false)

  // Upgrade tree state (persists across levels until all clones lost)
  const [upgradeState, setUpgradeState] = useState<UpgradeState>(initialUpgradeState)
  const persistedUpgradesRef = useRef<UpgradeState | null>(null)

  // ============================================================================
  // MULTIPLAYER STATE - Future-proofing for 2-player cooperative mode
  // ============================================================================
  // In single-player mode, creates 1 player in the array
  // In future coop mode, will have 2 players with separate inventories/upgrades
  const [multiplayerState, setMultiplayerState] = useState<MultiplayerState>({
    mode: "single",
    activePlayerId: null,
    players: [],
  })

  const waveSpawnerRef = useRef<WaveSpawner>(createWaveSpawner())
  const persistedTowersRef = useRef<Tower[]>([])
  const selectedHeroTypeRef = useRef<HeroType>("paladin")
  const pendingRewardsRef = useRef<DropPodRewards | null>(null)
  const persistedHeroRef = useRef<Hero | null>(null)
  const persistedInventoryRef = useRef<HeroInventory | null>(null)
  const persistedPlayersRef = useRef<Player[]>([]) // For multiplayer persistence (future)
  const persistedScoreRef = useRef<number>(0)
  const clearEnemiesRef = useRef<boolean>(false) // Signal to game loop to clear enemies immediately
  const pauseReasonRef = useRef<"user" | "system" | null>(null)
  const wasPlayingBeforeBackgroundRef = useRef<boolean>(false)
  // Synchronous mirror of stats.money for purchase flows. Affordability checks
  // against render-closure state are stale for a frame - two rapid taps could
  // both pass and drive money negative. Check + decrement this ref instead.
  const moneyRef = useRef<number>(0)

  // Keep the mirror in sync with committed state (loop income, interest, ...)
  useEffect(() => {
    moneyRef.current = stats.money
  }, [stats.money])

  // Synchronous spend/credit helpers - update the ref immediately so a second
  // purchase in the same tick sees the reduced balance
  const spendGoldSync = useCallback((amount: number) => {
    moneyRef.current -= amount
    setStats((prev) => ({ ...prev, money: prev.money - amount }))
  }, [])

  const creditGoldSync = useCallback((amount: number) => {
    moneyRef.current += amount
    setStats((prev) => ({ ...prev, money: prev.money + amount }))
  }, [])

  // ============================================================================
  // MULTIPLAYER STATE SYNCHRONIZATION
  // ============================================================================
  // Note: No automatic sync effects - multiplayerState is updated manually
  // when hero/inventory/upgradeState changes through action methods.
  // This avoids race conditions and circular dependencies.
  // Legacy state (hero, heroInventory, upgradeState) continues to work as before.

  // Calculate enemy progression level from starmap nodes completed
  // +1 level per 2 nodes completed, max +7
  const getProgressionLevel = useCallback((): number => {
    if (!mapState) return 0
    const nodesCompleted = mapState.visitedNodeIds.length
    if (nodesCompleted === 0) return 0
    return Math.min(ENEMY_PROGRESSION_MAX, Math.floor((nodesCompleted + 1) / 2))
  }, [mapState])

  const startGame = useCallback(() => {
    // Honours the menu's hero preference; "random" (the default) still re-rolls
    // on every run, and a pinned hero that no longer exists falls back to random.
    const availableHeroes = Object.keys(heroTypes) as HeroType[]
    selectedHeroTypeRef.current = resolveStartingHero(availableHeroes)

    // Clear any existing clones from previous games
    clearAllClones()

    setCurrentLevelIndex(0)
    setCurrentWaveIndex(0)
    setGameState("dropping")
    setEnemies([])
    setTowers([])
    persistedTowersRef.current = []
    setHero(null)
    setProjectiles([])
    setSelectedTowerId(null)
    setSelectedEnemyId(null)
    setShowNextWaveButton(false)
    setInterestAnimation(null)
    setFreePremiumTowers(0)
    setLandedPod(null)
    // Clear summons, corpses, and effects
    setSkillEffects([])
    setSummonedUnits([])
    setSelectedSummonId(null)
    setRootsEffects([])
    setFireEffects([])
    setCorpses([])
    setAoeVisualEffects([])
    setPendingAoeWaves([])
    setActiveItemEffects([])
    setPendingItemSlot(null)
    setDroppedItems([])
    setBarracksSoldiers([])
    setSelectedSoldierId(null)
    setIsSettingRallyPoint(false)
    setPendingRallyBarracksId(null)
    waveSpawnerRef.current = createWaveSpawner()
    // Reset upgrade tree for new game
    setUpgradeState(initialUpgradeState)
    persistedUpgradesRef.current = null
    // Reset star map so enemy progression scaling doesn't carry into a new run
    setMapState(null)
    setIsMapOpen(false)
    // Reset end-of-level flags so the shop/abduction flow can't trigger early
    setIsLastWaveComplete(false)
    setAbductionAnimation(null)
    setWaveStartTime(null)
    // Clear pause bookkeeping left over from a restart while (system-)paused
    pauseReasonRef.current = null
    wasPlayingBeforeBackgroundRef.current = false
  }, [])

  const selectHero = useCallback((heroType: HeroType) => {
    selectedHeroTypeRef.current = heroType
    setGameState("dropping")
  }, [])

  const onDropPodComplete = useCallback((rewards: DropPodRewards) => {
    pendingRewardsRef.current = rewards
    setGameState("landing")
  }, [])

  const onLandingComplete = useCallback(
    (landingPosition: Position) => {
      const currentLevel = levels[currentLevelIndex]
      const tileSize = gameSettings.canvas.tileSize

      // Track score at level start for abduction animation
      scoreAtLevelStartRef.current = persistedScoreRef.current

      setLandedPod({
        tileX: currentLevel.landingTile.x,
        tileY: currentLevel.landingTile.y,
        position: landingPosition,
      })

      // Position hero in front of (below) the droppod
      const heroTileX = currentLevel.landingTile.x
      const heroTileY = currentLevel.landingTile.y + 1

      const heroX = heroTileX * tileSize + tileSize / 2
      const heroY = heroTileY * tileSize + tileSize / 2

      const heroStartPos: Position = { x: heroX, y: heroY }
      let newHero: Hero
      let newInventory: HeroInventory
      if (persistedHeroRef.current) {
        newHero = {
          ...persistedHeroRef.current,
          position: heroStartPos,
          health: persistedHeroRef.current.maxHealth,
          mana: persistedHeroRef.current.maxMana,
          targetPosition: null,
          target: null,
          isAttacking: false,
          attackCooldown: 0,
          // Reset all skill cooldowns for the new level
          skills: persistedHeroRef.current.skills.map(skill => ({
            ...skill,
            currentCooldown: 0,
          })),
        }
        // Use persisted inventory if available
        newInventory = persistedInventoryRef.current || createEmptyInventory()
      } else {
        newHero = createHero(selectedHeroTypeRef.current, heroStartPos)
        // Initialize with 6 random unique items for new hero
        newInventory = createStartingInventory()
      }

      const rewards = pendingRewardsRef.current || { coins: 0, lives: 0, freePremiumTowers: 0 }

      setGameState("playing")
      setStats({
        money: currentLevel.startMoney + rewards.coins,
        lives: currentLevel.startLives + rewards.lives,
        wave: 1,
        score: persistedScoreRef.current,
      })
      setHero(newHero)
      persistedHeroRef.current = newHero
      setHeroInventory(newInventory)
      persistedInventoryRef.current = newInventory
      setSelectedHeroId(newHero.id)
      setFreePremiumTowers(rewards.freePremiumTowers)

      // Initialize multiplayer state with single player
      // TODO: In multiplayer mode, restore players from persistedPlayersRef
      // For now, creates new player from persistedHeroRef and persistedInventoryRef (single-player)
      // Use persisted upgrade state if available, otherwise use current
      const playerUpgradeState = persistedUpgradesRef.current || upgradeState
      const player = createPlayer("player1", "Player 1", newHero, newInventory, playerUpgradeState)
      setMultiplayerState({
        mode: "single",
        activePlayerId: "player1",
        players: [player],
      })
      // TODO: Store player in persistedPlayersRef for future multiplayer persistence
      persistedPlayersRef.current = [player]

      waveSpawnerRef.current = createWaveSpawner()
      setShowNextWaveButton(true)

      // Reset heroism level for new level
      setHeroismLevel(0)

      // Handle pending item drop from pelf event (when inventory was full)
      if (mapState?.pendingItemDrop) {
        const itemId = mapState.pendingItemDrop
        // Create dropped item near the hero position
        const dropPosition = {
          x: heroX + (Math.random() - 0.5) * 40,
          y: heroY + 30, // Slightly below the hero
        }
        const droppedItem = createDroppedItem(dropPosition, itemId)
        setDroppedItems([droppedItem])
        // Clear the pending drop from map state
        setMapState({ ...mapState, pendingItemDrop: undefined })
      }
    },
    [currentLevelIndex, mapState],
  )

  const placeTower = useCallback(
    (type: TowerType, position: Position, useFreeToken = false, _playerId?: PlayerId): boolean => {
      // TODO: Use playerId in multiplayer mode to track tower ownership
      // For now, always uses active player (single-player mode)
      const cost = getTowerCost(type, 1, heroismLevel)

      const towerConfig = towerTypes[type]
      const isPremium = towerConfig?.isPremium

      if (isPremium && useFreeToken && freePremiumTowers > 0) {
        // Use free token
      } else if (moneyRef.current < cost) {
        return false
      }

      if (landedPod) {
        const tileSize = gameSettings.canvas.tileSize
        const tileX = Math.floor(position.x / tileSize)
        const tileY = Math.floor(position.y / tileSize)
        if (tileX === landedPod.tileX && tileY === landedPod.tileY) {
          return false
        }
      }

      if (!canPlaceTower(position, type, towers, level.path, level.pathWidth, level.tilemap?.objectDecorations, level.tilemap?.decorations)) {
        return false
      }

      const newTower = createTower(type, position)
      // Building a tower unlocks its codex entry for good
      discover("towers", type)
      setTowers((prev) => {
        const updated = [...prev, newTower]
        persistedTowersRef.current = updated
        return updated
      })
      playTowerBuildSound()

      if (isPremium && useFreeToken && freePremiumTowers > 0) {
        setFreePremiumTowers((prev) => prev - 1)
      } else {
        spendGoldSync(cost)
      }

      const tileSize = gameSettings.canvas.tileSize
      const particleEffect: ParticleEffect = {
        id: generateId("particle"),
        position: newTower.position,
        size: {
          width: newTower.tileWidth * tileSize,
          height: newTower.tileHeight * tileSize,
        },
        colors: ["#d1d5db", "#9ca3af", "#6b7280", "#e5e7eb"],
        timestamp: gameNow(),
      }
      setParticleEffects((prev) => [...prev, particleEffect])

      return true
    },
    [stats.money, towers, level, freePremiumTowers, landedPod],
  )

  const upgradeTowerById = useCallback(
    (towerId: string, _playerId?: PlayerId): boolean => {
      // TODO: Use playerId in multiplayer mode to verify tower ownership
      // For now, always uses active player (single-player mode)
      const tower = towers.find((t) => t.id === towerId)
      if (!tower) return false

      const upgrade = upgradeTower(tower, heroismLevel)
      if (!upgrade) return false

      if (moneyRef.current < upgrade.cost) return false

      setTowers((prev) => {
        const updated = prev.map((t) => (t.id === towerId ? upgrade.tower : t))
        persistedTowersRef.current = updated
        return updated
      })
      spendGoldSync(upgrade.cost)
      playTowerBuildSound()

      // If this is a barracks, upgrade soldiers, heal them to full, respawn dead ones, and show god ray effects
      if (tower.type === "barracks") {
        const newLevel = upgrade.tower.level
        // Get all soldiers for this barracks (including dead ones for respawn)
        const soldiersForBarracks = barracksSoldiers.filter(
          (s) => s.barracksTowerId === towerId
        )

        // Calculate scale based on new level (matching rendering.ts soldier scaling)
        const SOLDIER_BASE_SCALE = 0.85
        const SOLDIER_SCALE_PER_LEVEL = 0.05
        const levelScale = SOLDIER_BASE_SCALE + (newLevel - 1) * SOLDIER_SCALE_PER_LEVEL

        // Create god ray particle effects for each soldier (alive or being respawned)
        const newEffects: ParticleEffect[] = soldiersForBarracks.map((soldier) => ({
          id: generateId("soldier-upgrade"),
          position: soldier.isDead ? upgrade.tower.position : soldier.position,
          size: { width: 48, height: 48 },
          colors: ["#ffb800", "#ffd000", "#ffe066"],
          timestamp: gameNow(),
          type: "soldier-upgrade" as const,
          scale: levelScale,
        }))

        if (newEffects.length > 0) {
          setParticleEffects((prev) => [...prev, ...newEffects])
        }

        // Create soldier spawn portrait animation floating above barracks (shows new soldier type)
        const upgradePortrait: SoldierSpawnPortrait = {
          id: generateId("spawn-portrait"),
          soldierId: "upgrade", // Special ID for upgrade-triggered portrait
          towerId: towerId,
          towerPosition: upgrade.tower.position,
          barrackLevel: newLevel,
          startTime: gameNow(),
        }
        setSoldierSpawnPortraits((prev) => [...prev, upgradePortrait])

        // Update soldier stats for the new level, heal to full, and respawn dead soldiers
        setBarracksSoldiers((prev) => {
          return prev.map((soldier) => {
            if (soldier.barracksTowerId !== towerId) return soldier
            // Import config from barracks-config
            const config = getFootmanConfig(newLevel)
            // Heal to full health and respawn dead soldiers
            return {
              ...soldier,
              maxHealth: config.health,
              health: config.health, // Full health on upgrade
              damage: config.damage,
              armor: config.armor + 2, // +2 bonus armor on upgrade
              speed: config.speed,
              // Respawn dead soldiers
              isDead: false,
              deathTime: null,
              respawnTime: null,
              spawnTime: soldier.isDead ? gameNow() : soldier.spawnTime, // Reset spawn time for respawned soldiers (fade-in effect)
            }
          })
        })
      }

      return true
    },
    [towers, stats.money, barracksSoldiers],
  )

  const sellTower = useCallback(
    (towerId: string, _playerId?: PlayerId) => {
      // TODO: Use playerId in multiplayer mode to refund to tower owner
      // For now, always uses active player (single-player mode)
      const tower = towers.find((t) => t.id === towerId)
      if (!tower) return

      // If it's a barracks, remove its soldiers
      if (tower.type === "barracks") {
        setBarracksSoldiers((prev) =>
          prev.filter((s) => s.barracksTowerId !== towerId)
        )
      }

      // Calculate cumulative investment (all upgrades from level 1 to current level)
      const config = towerTypes[tower.type]
      const totalInvested = config.upgrades.slice(0, tower.level).reduce((sum, upgrade) => sum + upgrade.cost, 0)
      const refund = Math.floor(totalInvested * 0.7)

      const tileSize = gameSettings.canvas.tileSize
      const particleEffect: ParticleEffect = {
        id: generateId("particle"),
        position: tower.position,
        size: {
          width: tower.tileWidth * tileSize,
          height: tower.tileHeight * tileSize,
        },
        colors: ["#fbbf24", "#f59e0b", "#d97706", "#fde047"],
        timestamp: gameNow(),
      }
      setParticleEffects((prev) => [...prev, particleEffect])

      setTowers((prev) => {
        const updated = prev.filter((t) => t.id !== towerId)
        persistedTowersRef.current = updated
        return updated
      })
      creditGoldSync(refund)
      setSelectedTowerId(null)
      playSellSound()

      // Show sell animation (yellow text next to gold counter)
      setSellAnimation(refund)
      setTimeout(() => setSellAnimation(null), 2000)
    },
    [towers],
  )

  const moveHero = useCallback(
    (destination: Position, _playerId?: PlayerId) => {
      // TODO: Use playerId in multiplayer mode to move specific player's hero
      // For now, always uses active player (single-player mode)
      if (!hero) return

      if (landedPod) {
        const tileSize = gameSettings.canvas.tileSize
        const destTileX = Math.floor(destination.x / tileSize)
        const destTileY = Math.floor(destination.y / tileSize)
        if (destTileX === landedPod.tileX && destTileY === landedPod.tileY) {
          return
        }
      }

      // Cancel any pending skill execution when hero is explicitly moved
      if (pendingSkillExecution) {
        setPendingSkillExecution(null)
      }

      const updatedHero = setHeroDestination(hero, destination)
      setHero({
        ...updatedHero,
        isAttacking: false,
        target: null,
      })
    },
    [hero, landedPod, pendingSkillExecution],
  )

  const removeParticleEffect = useCallback((effectId: string) => {
    setParticleEffects((prev) => prev.filter((e) => e.id !== effectId))
  }, [])

  // Trigger screen shake effect
  const triggerScreenShake = useCallback((duration = 500, intensity = 8) => {
    // Clear any existing shake
    if (screenShakeTimeoutRef.current) {
      clearTimeout(screenShakeTimeoutRef.current)
    }

    setIsScreenShaking(true)
    const startTime = gameNow()

    const shake = () => {
      const elapsed = gameNow() - startTime
      const remaining = 1 - elapsed / duration

      if (remaining > 0) {
        const currentIntensity = remaining * intensity
        setScreenShakeOffset({
          x: (Math.random() - 0.5) * currentIntensity,
          y: (Math.random() - 0.5) * currentIntensity,
        })
        screenShakeTimeoutRef.current = setTimeout(shake, 16) // ~60fps
      } else {
        setScreenShakeOffset({ x: 0, y: 0 })
        setIsScreenShaking(false)
      }
    }

    shake()
  }, [])

  const activateHeroSkill = useCallback(
    (skillId: string, _playerId?: PlayerId): boolean => {
      // TODO: Use playerId in multiplayer mode to activate specific player's skill
      // For now, always uses active player (single-player mode)
      if (!hero) return false

      // If clicking the same skill that's pending, cancel targeting mode
      if (pendingSkillId === skillId) {
        setPendingSkillId(null)
        return true
      }

      const { canUse } = canUseSkill(hero, skillId)
      if (!canUse) return false

      const skill = hero.skills.find((s) => s.id === skillId)
      if (!skill) return false

      // If skill is targetable, set it as pending and wait for click
      if (skill.isTargetable) {
        setPendingSkillId(skillId)
        return true
      }

      if (skill.type === "skillshot") {
        const { hero: updatedHero, effect } = activateSkillshot(hero, skillId)
        if (effect) {
          playPaladinSpectralBladeSound()
          setHero(updatedHero)
          setSkillEffects((prev) => [...prev, effect])
        }
        return true
      }

      if (skill.type === "dash") {
        const updatedHero = activateDash(hero, skillId)
        if (updatedHero) {
          playPaladinVanguardRushTravelSound()
          setHero(updatedHero)
          persistedHeroRef.current = updatedHero
          return true
        }
        return false
      }

      if (skill.type === "summon") {
        const { hero: updatedHero, newSummons, removedSummonIds } = activateSummon(
          hero,
          skillId,
          summonedUnits,
        )
        playRangerWildkinCallSound()
        setHero(updatedHero)
        persistedHeroRef.current = updatedHero

        // Remove old summons and add new ones
        setSummonedUnits((prev) => {
          const filtered = prev.filter((s) => !removedSummonIds.includes(s.id))
          return [...filtered, ...newSummons]
        })
        return true
      }

      if (skill.type === "aoe") {
        const { hero: updatedHero, affectedEnemies, pendingWaves, visualEffects } = activateAOE(hero, skillId, enemies)
        setHero(updatedHero)
        persistedHeroRef.current = updatedHero

        // Play tremor slam sound 3 times with 300ms between each (one for each seismic wave)
        playSiegesmithTremorSlamSound()
        setTimeout(() => playSiegesmithTremorSlamSound(), 300)
        setTimeout(() => playSiegesmithTremorSlamSound(), 600)

        // Trigger screen shake for tremor slam
        triggerScreenShake(500, 6)

        // Update affected enemies from first wave damage
        if (affectedEnemies.length > 0) {
          setEnemies((prev) => {
            return prev.map((enemy) => {
              const affected = affectedEnemies.find((a) => a.id === enemy.id)
              return affected || enemy
            })
          })
        }

        // Add pending waves for delayed damage
        if (pendingWaves.length > 0) {
          setPendingAoeWaves((prev) => [...prev, ...pendingWaves])
        }

        // Add visual effects for all three waves
        if (visualEffects.length > 0) {
          setAoeVisualEffects((prev) => [...prev, ...visualEffects])
        }

        return true
      }

      if (skill.type === "buff") {
        const updatedHero = activateBuff(hero, skillId)
        if (updatedHero) {
          setHero(updatedHero)
          persistedHeroRef.current = updatedHero
          return true
        }
        return false
      }

      return false
    },
    [hero, summonedUnits, enemies, pendingSkillId, triggerScreenShake],
  )

  // Helper to execute a skill immediately (used by both executeTargetedSkill and when hero reaches range)
  const executeSkillAtPosition = useCallback(
    (skillId: string, targetPosition: Position, targetEnemyId?: string, _playerId?: PlayerId, heroOverride?: Hero): boolean => {
      // TODO: Use playerId in multiplayer mode to execute specific player's skill
      // For now, always uses active player (single-player mode)
      // heroOverride lets callers pass an already-updated hero (e.g. one that
      // was just stopped) so the activation doesn't clobber that update
      const activeHero = heroOverride ?? hero
      if (!activeHero) return false

      const skill = activeHero.skills.find((s) => s.id === skillId)
      if (!skill) return false

      if (skill.type === "dash") {
        const updatedHero = activateTargetedDash(activeHero, skillId, targetPosition)
        if (updatedHero) {
          setHero(updatedHero)
          persistedHeroRef.current = updatedHero
          return true
        }
        return false
      }

      if (skill.type === "projectile") {
        const { hero: updatedHero, effect } = activateTargetedProjectile(activeHero, skillId, targetPosition)
        setHero(updatedHero)
        persistedHeroRef.current = updatedHero
        if (effect) {
          setSkillEffects((prev) => [...prev, effect])
        }
        return true
      }

      // Handle targeted-dot skill (Entangling Roots) - ground targeting
      if (skill.type === "targeted-dot") {
        const { hero: updatedHero, rootsEffect, updatedEnemy } = activateEntanglingRoots(
          activeHero,
          skillId,
          targetPosition,
          enemies,
        )

        if (rootsEffect) {
          playRangerEntangledRootsSound()
          setHero(updatedHero)
          persistedHeroRef.current = updatedHero

          // Update the affected enemy if there was one at target position
          if (updatedEnemy) {
            setEnemies((prev) =>
              prev.map((e) => (e.id === updatedEnemy.id ? updatedEnemy : e)),
            )
          }

          // Add the roots effect
          setRootsEffects((prev) => [...prev, rootsEffect])
          return true
        }
        return false
      }

      // Handle targeted summon skill (Forge Turret) - spawns at clicked location
      if (skill.type === "summon") {
        const { hero: updatedHero, newSummons, removedSummonIds } = activateSummon(
          activeHero,
          skillId,
          summonedUnits,
          targetPosition,
        )

        if (newSummons.length > 0 || removedSummonIds.length > 0) {
          setHero(updatedHero)
          persistedHeroRef.current = updatedHero

          // Remove old summons and add new ones
          setSummonedUnits((prev) => {
            const filtered = prev.filter((s) => !removedSummonIds.includes(s.id))
            return [...filtered, ...newSummons]
          })

          // Play forge turret sound for siegesmith
          if (skillId === "forge-turret" && newSummons.length > 0) {
            playSiegesmithForgeTurretSound()
          }
          return true
        }
        return false
      }

      return false
    },
    [hero, enemies, summonedUnits],
  )

  // Execute a pending targetable skill with a target position or enemy
  const executeTargetedSkill = useCallback(
    (targetPosition: Position, targetEnemyId?: string, _playerId?: PlayerId): boolean => {
      // TODO: Use playerId in multiplayer mode to execute specific player's skill
      // For now, always uses active player (single-player mode)
      if (!hero || !pendingSkillId) return false

      const skill = hero.skills.find((s) => s.id === pendingSkillId)
      if (!skill) {
        setPendingSkillId(null)
        return false
      }

      // Get skill range
      const stats = getSkillLevelStats(skill)
      const rangePixels = (stats.range || 8) * TILE_SIZE
      const distance = Math.sqrt(distanceSquared(hero.position, targetPosition))

      // Clear pending skill ID (we're handling it now)
      setPendingSkillId(null)

      // If out of range, queue skill and move hero toward target
      if (distance > rangePixels) {
        setPendingSkillExecution({
          skillId: skill.id,
          targetPosition,
          targetEnemyId,
          requiredRange: rangePixels,
        })
        // Move hero toward target position
        const heroWithDestination = setHeroDestination(hero, targetPosition)
        setHero(heroWithDestination)
        persistedHeroRef.current = heroWithDestination
        return true // Return true to indicate skill is queued
      }

      // In range - execute immediately
      return executeSkillAtPosition(skill.id, targetPosition, targetEnemyId)
    },
    [hero, pendingSkillId, executeSkillAtPosition],
  )

  const cancelPendingSkill = useCallback(() => {
    setPendingSkillId(null)
  }, [])

  // Check if hero has reached range for pending skill execution
  useEffect(() => {
    if (!pendingSkillExecution || !hero || !hero.position) return

    const distance = Math.sqrt(distanceSquared(hero.position, pendingSkillExecution.targetPosition))

    if (distance <= pendingSkillExecution.requiredRange) {
      // Hero is in range - stop movement and execute skill
      const stoppedHero = { ...hero, targetPosition: null, isMoving: false }
      setHero(stoppedHero)
      persistedHeroRef.current = stoppedHero

      // Execute the queued skill from the stopped hero so the activation
      // doesn't resurrect the discarded targetPosition (hero kept walking)
      executeSkillAtPosition(
        pendingSkillExecution.skillId,
        pendingSkillExecution.targetPosition,
        pendingSkillExecution.targetEnemyId,
        undefined,
        stoppedHero,
      )

      // Clear the pending execution
      setPendingSkillExecution(null)
    }
  }, [hero?.position?.x, hero?.position?.y, pendingSkillExecution, executeSkillAtPosition])

  const useSkillByHotkey = useCallback(
    (hotkey: string, playerId?: PlayerId): boolean => {
      // TODO: Use playerId in multiplayer mode to use specific player's skill
      // For now, always uses active player (single-player mode)
      if (!hero) return false
      const skill = getSkillByHotkey(hero, hotkey)
      if (!skill) return false
      return activateHeroSkill(skill.id, playerId)
    },
    [hero, activateHeroSkill],
  )

  const upgradeHeroSkill = useCallback(
    (skillId: string, _playerId?: PlayerId): boolean => {
      // TODO: Use playerId in multiplayer mode to upgrade specific player's hero skill
      // For now, always uses active player (single-player mode)
      if (!hero) return false

      const skill = hero.skills.find((s) => s.id === skillId)
      if (!skill || skill.currentLevel >= skill.maxLevel) return false

      // Check gold cost for skills like Dwarven Treasury
      const goldCost = getSkillGoldCost(hero, skillId)
      if (goldCost > 0 && moneyRef.current < goldCost) return false

      // Check mana cost for passive skill upgrades
      const manaCost = getSkillUpgradeManaCost(hero, skillId)
      if (manaCost > 0 && hero.mana < manaCost) return false

      const upgraded = upgradeSkill(hero, skillId)
      if (!upgraded) return false

      // Deduct gold if required
      if (goldCost > 0) {
        spendGoldSync(goldCost)
      }

      // Deduct mana if required (for passive skill upgrades)
      let finalHero = upgraded
      if (manaCost > 0) {
        finalHero = { ...upgraded, mana: upgraded.mana - manaCost }
      }

      setHero(finalHero)
      persistedHeroRef.current = finalHero

      // Set the upgraded skill ID for animation, then clear after animation duration
      setLastUpgradedSkillId(skillId)
      setTimeout(() => setLastUpgradedSkillId(null), 600) // Clear after animation

      playSkillLevelUpSound()
      return true
    },
    [hero, stats.money],
  )

  const selectEnemy = useCallback(
    (enemyId: string | null) => {
      setSelectedEnemyId(enemyId)
      // Deselect hero when selecting an enemy
      if (enemyId) {
        setSelectedHeroId(null)
      }
    },
    [],
  )

  const deselectEnemy = useCallback(() => {
    setSelectedEnemyId(null)
  }, [])

  const selectSummon = useCallback(
    (summonId: string | null) => {
      setSelectedSummonId(summonId)
      // Deselect hero and enemy when selecting a summon
      if (summonId) {
        setSelectedHeroId(null)
        setSelectedEnemyId(null)
      }
    },
    [],
  )

  const deselectSummon = useCallback(() => {
    setSelectedSummonId(null)
  }, [])

  const selectSoldier = useCallback(
    (soldierId: string | null) => {
      setSelectedSoldierId(soldierId)
      // Deselect hero, enemy, and summon when selecting a soldier
      if (soldierId) {
        setSelectedHeroId(null)
        setSelectedEnemyId(null)
        setSelectedSummonId(null)
        setSelectedTowerId(null)
      }
    },
    [],
  )

  const deselectSoldier = useCallback(() => {
    setSelectedSoldierId(null)
  }, [])

  const moveSummon = useCallback(
    (summonId: string, destination: Position) => {
      setSummonedUnits((prev) =>
        prev.map((summon) =>
          summon.id === summonId ? setSummonDestination(summon, destination) : summon,
        ),
      )
    },
    [],
  )

  // Use an item from inventory
  const useItem = useCallback(
    (slotIndex: number, _playerId?: PlayerId): boolean => {
      // TODO: Use playerId in multiplayer mode to use specific player's item
      // For now, always uses active player (single-player mode)
      if (!hero) return false

      // If clicking the same item that's pending, cancel targeting mode
      if (pendingItemSlot === slotIndex) {
        setPendingItemSlot(null)
        return true
      }

      const slot = heroInventory.slots[slotIndex]
      if (!slot) return false

      const itemDef = getItemDefinition(slot.itemDefId)
      if (!itemDef) return false

      // Equipment items provide passive bonuses while in inventory - they cannot be "used"
      // Just return false to indicate no action taken (they stay in inventory)
      if (itemDef.type === "equipment") {
        return false
      }

      // Check if consumable item can be used (cooldown check)
      if (!canUseItem(heroInventory, slotIndex)) return false

      // Check if item requires targeting (teleport, lure)
      const requiresTargeting = itemDef.effects.some(
        (e) => e.type === "teleport" || e.type === "lure"
      )

      if (requiresTargeting) {
        // Enter targeting mode for this item
        setPendingItemSlot(slotIndex)
        // Also cancel any pending skill
        setPendingSkillId(null)
        return true
      }

      // Use consumable item immediately
      const result = consumeInventoryItem(hero, heroInventory, slotIndex)
      if (result.consumed) {
        setHero(result.hero)
        persistedHeroRef.current = result.hero
        setHeroInventory(result.inventory)
        persistedInventoryRef.current = result.inventory

        // Add any active effects
        if (result.activeEffects.length > 0) {
          setActiveItemEffects((prev) => [...prev, ...result.activeEffects])
        }

        // Play potion sound for consumables
        playPotionSound()

        // Add particle effect for consumable (colors based on effect type)
        const effectTypes = itemDef.effects.map((e) => e.type)
        setParticleEffects((prev) => [
          ...prev,
          {
            id: generateId("particle"),
            position: { ...hero.position },
            size: { width: 48, height: 48 },
            colors: getItemEffectColors(effectTypes),
            timestamp: gameNow(),
          },
        ])
        return true
      }

      return false
    },
    [hero, heroInventory, pendingItemSlot],
  )

  // Execute a targeted item (teleport to position, or lure at position)
  const executeTargetedItem = useCallback(
    (targetPosition: Position, _playerId?: PlayerId): boolean => {
      // TODO: Use playerId in multiplayer mode to execute specific player's item
      // For now, always uses active player (single-player mode)
      if (!hero || pendingItemSlot === null) return false

      const slot = heroInventory.slots[pendingItemSlot]
      if (!slot) {
        setPendingItemSlot(null)
        return false
      }

      const itemDef = getItemDefinition(slot.itemDefId)
      if (!itemDef) {
        setPendingItemSlot(null)
        return false
      }

      // Clear pending item
      const slotToUse = pendingItemSlot
      setPendingItemSlot(null)

      // Check for teleport effect
      const teleportEffect = itemDef.effects.find((e) => e.type === "teleport")
      if (teleportEffect) {
        // Save original position for departure effect
        const departurePosition = { ...hero.position }

        // Teleport hero to target position
        const updatedHero = {
          ...hero,
          position: { ...targetPosition },
          targetPosition: null,
          target: null,
        }
        setHero(updatedHero)
        persistedHeroRef.current = updatedHero

        // Consume the item
        const updatedInventory = removeItemFromInventory(heroInventory, slotToUse, 1)
        // Set cooldown on the slot if it still exists
        if (updatedInventory.slots[slotToUse]) {
          updatedInventory.slots[slotToUse] = {
            ...updatedInventory.slots[slotToUse]!,
            currentCooldown: itemDef.cooldown || 0,
          }
        }
        setHeroInventory(updatedInventory)
        persistedInventoryRef.current = updatedInventory

        // Add particle effects at both departure and arrival positions (cyan)
        setParticleEffects((prev) => [
          ...prev,
          // Departure effect (fading out)
          {
            id: generateId("particle"),
            position: departurePosition,
            size: { width: 48, height: 48 },
            colors: ["#00CED1", "#20B2AA", "#5F9EA0", "#48D1CC"], // Cyan/teal
            timestamp: gameNow(),
          },
          // Arrival effect (appearing)
          {
            id: generateId("particle"),
            position: { ...targetPosition },
            size: { width: 48, height: 48 },
            colors: ["#00CED1", "#20B2AA", "#5F9EA0", "#48D1CC"], // Cyan/teal
            timestamp: gameNow(),
          },
        ])
        return true
      }

      // Check for lure effect
      const lureEffect = itemDef.effects.find((e) => e.type === "lure")
      if (lureEffect) {
        // Use the item and create lure effect at target position
        const result = consumeInventoryItem(hero, heroInventory, slotToUse)
        if (result.consumed) {
          setHero(result.hero)
          persistedHeroRef.current = result.hero
          setHeroInventory(result.inventory)
          persistedInventoryRef.current = result.inventory

          // Add lure effect with the target position
          for (const effect of result.activeEffects) {
            if (effect.effectType === "lure") {
              setActiveItemEffects((prev) => [
                ...prev,
                { ...effect, targetPosition: targetPosition },
              ])
            } else {
              setActiveItemEffects((prev) => [...prev, effect])
            }
          }

          // Add particle effects - on hero (throwing) and at lure position (landing)
          setParticleEffects((prev) => [
            ...prev,
            // Effect on hero showing they threw something
            {
              id: generateId("particle"),
              position: { ...hero.position },
              size: { width: 32, height: 32 },
              colors: ["#DAA520", "#FFD700"], // Golden
              timestamp: gameNow(),
            },
            // Effect at lure position (golden)
            {
              id: generateId("particle"),
              position: { ...targetPosition },
              size: { width: 48, height: 48 },
              colors: ["#DAA520", "#FFD700", "#FFA500", "#FF8C00"], // Golden/orange
              timestamp: gameNow(),
            },
          ])
          return true
        }
      }

      return false
    },
    [hero, heroInventory, pendingItemSlot],
  )

  // Cancel pending item targeting
  const cancelPendingItem = useCallback(() => {
    setPendingItemSlot(null)
  }, [])

  // Drop an item from inventory onto the ground
  const dropItemFromInventory = useCallback(
    (slotIndex: number, _playerId?: PlayerId): boolean => {
      // TODO: Use playerId in multiplayer mode to drop from specific player's inventory
      // For now, always uses active player (single-player mode)
      const slot = heroInventory.slots[slotIndex]
      if (!slot || !hero) return false

      // Cannot drop the Clone Activation Codex
      if (isCloneBeacon(slot.itemDefId)) return false

      // Create a dropped item at hero's position with slight random offset
      const droppedItem: DroppedItem = {
        id: generateId(),
        itemDefId: slot.itemDefId,
        position: {
          x: hero.position.x + (Math.random() - 0.5) * 40,
          y: hero.position.y + (Math.random() - 0.5) * 40,
        },
        createdAt: gameNow(),
      }

      setDroppedItems((prev) => [...prev, droppedItem])

      // Play armor drop sound for non-consumable items
      const itemDef = getItemDefinition(slot.itemDefId)
      if (itemDef && itemDef.type !== "consumable") {
        playArmorDropSound()
      }

      // Remove only 1 item from the slot
      const updatedInventory = removeItemFromInventory(heroInventory, slotIndex, 1)
      setHeroInventory(updatedInventory)
      persistedInventoryRef.current = updatedInventory
      return true
    },
    [heroInventory, hero],
  )

  // Pick up a dropped item from the ground into inventory
  const pickupDroppedItem = useCallback(
    (droppedItemId: string, _playerId?: PlayerId): boolean => {
      // TODO: Use playerId in multiplayer mode to pick up into specific player's inventory
      // For now, always uses active player (single-player mode)
      const droppedItem = droppedItems.find((item) => item.id === droppedItemId)
      if (!droppedItem) return false

      const itemDef = getItemDefinition(droppedItem.itemDefId)
      if (!itemDef) return false

      // Check if inventory has space
      // First try to stack with existing item
      const existingSlotIndex = heroInventory.slots.findIndex(
        (slot) => slot && slot.itemDefId === droppedItem.itemDefId && itemDef.stackable && slot.quantity < itemDef.maxStack
      )

      if (existingSlotIndex !== -1) {
        // Stack with existing item
        const updatedInventory = { ...heroInventory }
        updatedInventory.slots = [...heroInventory.slots]
        updatedInventory.slots[existingSlotIndex] = {
          ...updatedInventory.slots[existingSlotIndex]!,
          quantity: updatedInventory.slots[existingSlotIndex]!.quantity + 1,
        }
        setHeroInventory(updatedInventory)
        persistedInventoryRef.current = updatedInventory
        setDroppedItems((prev) => prev.filter((item) => item.id !== droppedItemId))
        // Play armor equip sound for non-consumable items
        if (itemDef.type !== "consumable") {
          playArmorEquipSound()
        }
        return true
      }

      // Try to find an empty slot
      const emptySlotIndex = heroInventory.slots.findIndex((slot) => slot === null)
      if (emptySlotIndex === -1) {
        // Inventory is full
        return false
      }

      // Add to empty slot
      const updatedInventory = { ...heroInventory }
      updatedInventory.slots = [...heroInventory.slots]
      updatedInventory.slots[emptySlotIndex] = {
        id: generateId(),
        itemDefId: droppedItem.itemDefId,
        quantity: 1,
        currentCooldown: 0,
      }
      setHeroInventory(updatedInventory)
      persistedInventoryRef.current = updatedInventory
      setDroppedItems((prev) => prev.filter((item) => item.id !== droppedItemId))
      // Play armor equip sound for non-consumable items
      if (itemDef.type !== "consumable") {
        playArmorEquipSound()
      }
      return true
    },
    [droppedItems, heroInventory],
  )

  // Destroy a dropped item (remove it from the world)
  const destroyDroppedItem = useCallback(
    (droppedItemId: string): boolean => {
      const droppedItem = droppedItems.find((item) => item.id === droppedItemId)
      if (!droppedItem) return false

      setDroppedItems((prev) => prev.filter((item) => item.id !== droppedItemId))
      return true
    },
    [droppedItems],
  )

  // Check if inventory has space for an item
  const canPickupItem = useCallback(
    (itemDefId: string): boolean => {
      const itemDef = getItemDefinition(itemDefId)
      if (!itemDef) return false

      // Check if can stack with existing
      const existingSlot = heroInventory.slots.find(
        (slot) => slot && slot.itemDefId === itemDefId && itemDef.stackable && slot.quantity < itemDef.maxStack
      )
      if (existingSlot) return true

      // Check for empty slot
      return heroInventory.slots.some((slot) => slot === null)
    },
    [heroInventory],
  )

  const pauseGame = useCallback(() => {
    pauseReasonRef.current = "user"
    wasPlayingBeforeBackgroundRef.current = false
    setGameState("paused")
  }, [])

  const resumeGame = useCallback(() => {
    pauseReasonRef.current = null
    wasPlayingBeforeBackgroundRef.current = false
    setGameState("playing")
  }, [])

  const pauseGameForSystem = useCallback(() => {
    if (gameState === "playing") {
      wasPlayingBeforeBackgroundRef.current = true
      pauseReasonRef.current = "system"
      setGameState("paused")
    }
  }, [gameState])

  const resumeGameFromSystem = useCallback(() => {
    if (
      gameState === "paused" &&
      pauseReasonRef.current === "system" &&
      wasPlayingBeforeBackgroundRef.current
    ) {
      pauseReasonRef.current = null
      wasPlayingBeforeBackgroundRef.current = false
      setGameState("playing")
    }
  }, [gameState])

  const restartGame = useCallback(() => {
    setFreePremiumTowers(0)
    setLandedPod(null)
    persistedHeroRef.current = null
    persistedInventoryRef.current = null
    persistedScoreRef.current = 0
    setHeroInventory(createEmptyInventory())
    startGame()
  }, [startGame])

  // Restart the game but keep the current hero and inventory
  const restartWithCurrentHero = useCallback(() => {
    if (!hero) {
      restartGame()
      return
    }

    // Persist current hero and inventory
    persistedHeroRef.current = hero
    persistedInventoryRef.current = heroInventory

    // Reset game state for level 1, wave 1
    setCurrentLevelIndex(0)
    setCurrentWaveIndex(0)
    setGameState("dropping")
    setEnemies([])
    setTowers([])
    persistedTowersRef.current = []
    setProjectiles([])
    setSelectedTowerId(null)
    setSelectedEnemyId(null)
    setShowNextWaveButton(false)
    setInterestAnimation(null)
    setFreePremiumTowers(0)
    setLandedPod(null)
    setSkillEffects([])
    setSummonedUnits([])
    setSelectedSummonId(null)
    setPendingSkillId(null)
    setRootsEffects([])
    setFireEffects([])
    setActiveItemEffects([])
    setPendingItemSlot(null)
    setCorpses([])
    setAoeVisualEffects([])
    setPendingAoeWaves([])
    setDroppedItems([])
    setBarracksSoldiers([])
    setIsSettingRallyPoint(false)
    setPendingRallyBarracksId(null)
    waveSpawnerRef.current = createWaveSpawner()
    // Reset end-of-level flags and pause bookkeeping (same as startGame)
    setIsLastWaveComplete(false)
    setAbductionAnimation(null)
    setWaveStartTime(null)
    pauseReasonRef.current = null
    wasPlayingBeforeBackgroundRef.current = false
  }, [hero, heroInventory, restartGame])

  // Buy an item from the Phase Terminal (adds to inventory if space, deducts gold)
  const buyItem = useCallback(
    (itemDefId: string, price: number, _playerId?: PlayerId): boolean => {
      // TODO: Use playerId in multiplayer mode to buy for specific player
      // For now, always uses active player (single-player mode)
      if (moneyRef.current < price) return false

      const itemDef = getItemDefinition(itemDefId)
      if (!itemDef) return false

      // Check if can stack with existing item
      const existingSlotIndex = heroInventory.slots.findIndex(
        (slot) => slot && slot.itemDefId === itemDefId && itemDef.stackable && slot.quantity < itemDef.maxStack
      )

      if (existingSlotIndex !== -1) {
        // Stack with existing item
        const updatedInventory = { ...heroInventory }
        updatedInventory.slots = [...heroInventory.slots]
        updatedInventory.slots[existingSlotIndex] = {
          ...updatedInventory.slots[existingSlotIndex]!,
          quantity: updatedInventory.slots[existingSlotIndex]!.quantity + 1,
        }
        setHeroInventory(updatedInventory)
        persistedInventoryRef.current = updatedInventory
        spendGoldSync(price)
        return true
      }

      // Find empty slot
      const emptySlotIndex = heroInventory.slots.findIndex((slot) => slot === null)
      if (emptySlotIndex === -1) {
        // Inventory full
        return false
      }

      // Add to empty slot
      const updatedInventory = { ...heroInventory }
      updatedInventory.slots = [...heroInventory.slots]
      updatedInventory.slots[emptySlotIndex] = {
        id: generateId(),
        itemDefId,
        quantity: 1,
        currentCooldown: 0,
      }
      setHeroInventory(updatedInventory)
      persistedInventoryRef.current = updatedInventory
      spendGoldSync(price)
      return true
    },
    [stats.money, heroInventory],
  )

  // Sell an item from the inventory (removes from inventory, adds gold)
  const sellItem = useCallback(
    (slotIndex: number, sellPrice: number): boolean => {
      const slot = heroInventory.slots[slotIndex]
      if (!slot) return false

      const itemDef = getItemDefinition(slot.itemDefId)
      if (!itemDef) return false

      // Cannot sell beacon
      if (isCloneBeacon(slot.itemDefId)) return false

      // Remove 1 item from the slot
      const updatedInventory = removeItemFromInventory(heroInventory, slotIndex, 1)
      setHeroInventory(updatedInventory)
      persistedInventoryRef.current = updatedInventory

      // Add gold
      creditGoldSync(sellPrice)

      // Play sell sound
      playSellSound()

      // Show sell animation (yellow text next to gold counter)
      setSellAnimation(sellPrice)
      setTimeout(() => setSellAnimation(null), 2000)

      return true
    },
    [heroInventory],
  )

  const purchaseHeroism = useCallback(
    (cost: number): boolean => {
      if (moneyRef.current < cost || heroismLevel >= 7) return false

      spendGoldSync(cost)
      setHeroismLevel((prev) => Math.min(prev + 1, 7))
      return true
    },
    [stats.money, heroismLevel],
  )

  /**
   * Buy a hero skill level at the shrine with gold (no skill point required).
   */
  const purchaseSkillUpgrade = useCallback(
    (skillId: string): boolean => {
      if (!hero) return false

      const skill = hero.skills.find((s) => s.id === skillId)
      if (!skill || skill.currentLevel >= skill.maxLevel) return false

      const cost = getShrineSkillUpgradeCost(skill.currentLevel)
      if (moneyRef.current < cost) return false

      const upgraded = upgradeSkillWithGold(hero, skillId)
      if (!upgraded) return false

      spendGoldSync(cost)
      setHero(upgraded)
      persistedHeroRef.current = upgraded

      setLastUpgradedSkillId(skillId)
      setTimeout(() => setLastUpgradedSkillId(null), 600)

      playSkillLevelUpSound()
      return true
    },
    [hero, stats.money],
  )

  // Spend gold (for shop refresh, etc.)
  const spendGold = useCallback(
    (amount: number): boolean => {
      if (moneyRef.current < amount) return false
      spendGoldSync(amount)
      return true
    },
    [stats.money],
  )

  // Trigger clone restore effect (called by game loop when hero is restored from clone)
  const triggerCloneRestoreEffect = useCallback((restoredLevel: number, heroPosition?: Position) => {
    setCloneRestoreEffect({ active: true, restoredLevel })

    // Spawn blue upward particles at hero position
    if (heroPosition) {
      const particleCount = 12
      for (let i = 0; i < particleCount; i++) {
        const offsetX = (Math.random() - 0.5) * 40
        const offsetY = (Math.random() - 0.5) * 40
        const particleEffect: ParticleEffect = {
          id: `clone-restore-${gameNow()}-${i}`,
          position: {
            x: heroPosition.x + offsetX,
            y: heroPosition.y + offsetY,
          },
          size: { width: 8, height: 8 },
          colors: ["#64B4FF", "#80C8FF", "#4090FF"], // Blue colors
          timestamp: gameNow() + i * 50, // Stagger particles
          type: "clone-restore",
        }
        setParticleEffects((prev) => [...prev, particleEffect])
      }
    }

    // Auto-clear after 2 seconds
    setTimeout(() => {
      setCloneRestoreEffect({ active: false, restoredLevel: null })
    }, 2000)
  }, [])

  // Unlock an upgrade from the upgrade tree
  const unlockUpgrade = useCallback(
    (upgradeId: string) => {
      setUpgradeState((prev) => {
        const newState = unlockUpgradeFromSystem(upgradeId, prev)
        persistedUpgradesRef.current = newState
        return newState
      })
    },
    []
  )

  const startNextLevel = useCallback(() => {
    const nextLevelIndex = currentLevelIndex + 1
    const nextLevel = getLevelByIndex(nextLevelIndex)

    if (!nextLevel) {
      setGameState("victory")
      return
    }

    // Interest is now applied when entering victory screen (setVictoryWithInterest)

    if (hero) {
      persistedHeroRef.current = hero
    }
    // Persist score across levels
    persistedScoreRef.current = stats.score

    setCurrentLevelIndex(nextLevelIndex)
    setCurrentWaveIndex(0)
    setGameState("dropping")
    setEnemies([])
    setProjectiles([])
    setSelectedTowerId(null)
    setShowNextWaveButton(false)
    setFreePremiumTowers(0)
    setLandedPod(null)
    setBarracksSoldiers([])

    setTowers([])
    persistedTowersRef.current = []
  }, [currentLevelIndex, hero, stats.score])

  // Start a specific level by index (used by map node selection)
  const startLevelByIndex = useCallback((levelIndex: number) => {
    const targetLevel = getLevelByIndex(levelIndex)
    if (!targetLevel) {
      console.warn("[Map] Invalid level index:", levelIndex)
      return
    }

    // Interest is now applied when entering victory screen (setVictoryWithInterest)

    if (hero) {
      persistedHeroRef.current = hero
    }
    // Persist score across levels
    persistedScoreRef.current = stats.score

    setCurrentLevelIndex(levelIndex)
    setCurrentWaveIndex(0)
    setGameState("dropping")
    setEnemies([])
    setProjectiles([])
    setSelectedTowerId(null)
    setShowNextWaveButton(false)
    setFreePremiumTowers(0)
    setLandedPod(null)
    setIsMapOpen(false)
    setBarracksSoldiers([])

    setTowers([])
    persistedTowersRef.current = []
  }, [hero, stats.score])

  // Generate a new map at game start
  const generateNewMap = useCallback(() => {
    const newMap = generateMap()
    setMapState(newMap)
    return newMap
  }, [])

  // Advance to a map node without starting the level (used when spaceship arrives)
  const advanceMapNode = useCallback((node: MapNode) => {
    // Functional update - several map actions can run in the same tick
    // (e.g. onNodeArrival + onCollectPelf), so never derive from a closure
    setMapState((prev) => {
      if (!prev || prev.currentNodeId === node.id) return prev
      return advanceToNode(prev, node.id)
    })
  }, [])

  // Handle node selection from the map (starts the level)
  const selectMapNode = useCallback((node: MapNode) => {
    if (!mapState) return

    // Only advance if not already at this node (may have been advanced by advanceMapNode)
    setMapState((prev) => {
      if (!prev || prev.currentNodeId === node.id) return prev
      return advanceToNode(prev, node.id)
    })

    // If event node (shop), keep map open so shop overlay appears on top
    if (node.type === "event") {
      // Shop is handled by game.tsx via setIsStarMapShopOpen
      // Keep map open so the shop overlay appears on top of the star map
      return
    }

    // Start the level
    if (node.levelIndex !== null && node.levelIndex !== undefined) {
      startLevelByIndex(node.levelIndex)
    }
  }, [mapState, startLevelByIndex])

  // Complete current map node (called after level victory)
  const completeMapNode = useCallback(() => {
    setMapState((prev) => {
      if (!prev || !prev.currentNodeId) return prev
      return completeCurrentNode(prev)
    })
  }, [])

  // Reveal an event node (changes eventRevealed to true)
  // Returns: { revealed: boolean, eventType: "shop" | "pelf", itemId?: string }
  const revealEventNode = useCallback((nodeId: string): { revealed: boolean; eventType?: "shop" | "pelf"; itemId?: string } => {
    if (!mapState) return { revealed: false }

    const node = mapState.nodes.find(n => n.id === nodeId)
    if (!node || node.type !== "event") return { revealed: false }

    // Already revealed?
    if (node.eventRevealed) {
      return { revealed: true, eventType: node.eventType, itemId: node.pelfItemId }
    }

    // Reveal the node
    setMapState((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        nodes: prev.nodes.map(n => (n.id === nodeId ? { ...n, eventRevealed: true } : n)),
      }
    })

    return { revealed: true, eventType: node.eventType, itemId: node.pelfItemId }
  }, [mapState])

  // Collect pelf reward from a node - add to inventory or set pending drop
  // Returns: { added: boolean, overflow: boolean }
  const collectPelfReward = useCallback((nodeId: string): { added: boolean; overflow: boolean; itemId?: string } => {
    if (!mapState) return { added: false, overflow: false }

    const node = mapState.nodes.find(n => n.id === nodeId)
    if (!node || node.type !== "event" || node.eventType !== "pelf" || !node.pelfItemId) {
      return { added: false, overflow: false }
    }

    // Try to add item to inventory
    const result = addItemToInventory(heroInventory, node.pelfItemId, 1)
    setHeroInventory(result.inventory)
    // Also update persisted ref so it's available when level starts
    persistedInventoryRef.current = result.inventory

    // If overflow, set pending drop in map state (functional update so we don't
    // clobber a same-tick advanceMapNode update)
    if (result.overflow > 0) {
      setMapState((prev) => (prev ? { ...prev, pendingItemDrop: node.pelfItemId } : prev))
      return { added: false, overflow: true, itemId: node.pelfItemId }
    }

    // Play armor equip sound for non-consumable items
    const itemDef = getItemDefinition(node.pelfItemId)
    if (itemDef && itemDef.type !== "consumable") {
      playArmorEquipSound()
    }

    return { added: true, overflow: false, itemId: node.pelfItemId }
  }, [mapState, heroInventory])

  // Open the map
  const openMap = useCallback(() => {
    setIsMapOpen(true)
  }, [])

  // Close the map
  const closeMap = useCallback(() => {
    setIsMapOpen(false)
  }, [])

  // Open map after completing a level (marks current node complete, opens map)
  const openMapAfterLevel = useCallback(() => {
    // Generate map if not exists (first level completion)
    if (!mapState) {
      const newMap = generateMap()
      // First level is node 0, mark it as visited
      const startNode = newMap.nodes.find(n => n.row === 0)
      if (startNode) {
        const mapWithStart = advanceToNode(newMap, startNode.id)
        const completedMap = completeCurrentNode(mapWithStart)
        setMapState(completedMap)
      } else {
        setMapState(newMap)
      }
    } else {
      // Complete current node
      const updatedMap = completeCurrentNode(mapState)
      setMapState(updatedMap)
    }

    // Set game state back to playing and open map
    setGameState("playing")
    setIsMapOpen(true)
  }, [mapState])

  const startNextWave = useCallback(() => {
    const nextWaveIndex = currentWaveIndex + 1

    // Guard against a stale/duplicated call past the last wave
    if (nextWaveIndex >= level.waves.length) return

    // Calculate interest with hero bonus (Dwarven Treasury)
    const interestBonus = hero ? getInterestBonus(hero) : 0
    const effectiveInterestRate = constants.INTEREST_RATE + interestBonus
    const interest = Math.floor(stats.money * effectiveInterestRate)

    setInterestAnimation(interest)
    setTimeout(() => setInterestAnimation(null), 2000)

    setStats((prev) => ({
      ...prev,
      money: prev.money + interest,
      wave: prev.wave + 1,
    }))
    setCurrentWaveIndex(nextWaveIndex)
    setShowNextWaveButton(false)
    setWaveStartTime(gameNow())
    setEnemies([])

    setHero((h) => (h ? healHero(h) : h))

    const spawnPos = level.path[0]
    const nodesCompleted = mapState?.visitedNodeIds.length ?? 0
    waveSpawnerRef.current = startWave(level.waves[nextWaveIndex], spawnPos, getProgressionLevel(), nodesCompleted)
    playNewWaveSound()
  }, [currentWaveIndex, level, stats.money, hero, getProgressionLevel, mapState])

  // Call next wave early - gives interest bonus as reward for fast play (only if within bonus window)
  const callWaveEarly = useCallback(
    (awardBonus: boolean = true) => {
      const nextWaveIndex = currentWaveIndex + 1

      // Can only call wave early if not on last wave and wave has been running for 5+ seconds
      if (nextWaveIndex >= level.waves.length) return
      if (!waveStartTime || gameNow() - waveStartTime < 5000) return

      // Only award interest if within the early call bonus window
      if (awardBonus) {
        // Calculate interest with hero bonus (Dwarven Treasury)
        const interestBonus = hero ? getInterestBonus(hero) : 0
        const effectiveInterestRate = constants.INTEREST_RATE + interestBonus
        const interest = Math.floor(stats.money * effectiveInterestRate)

        setInterestAnimation(interest)
        setTimeout(() => setInterestAnimation(null), 2000)

        // Update stats: add interest, increment wave
        setStats((prev) => ({
          ...prev,
          money: prev.money + interest,
          wave: prev.wave + 1,
        }))
      } else {
        // No bonus - just increment wave
        setStats((prev) => ({
          ...prev,
          wave: prev.wave + 1,
        }))
      }

      // Don't clear enemies - let them continue while new wave spawns

      setCurrentWaveIndex(nextWaveIndex)
      setWaveStartTime(gameNow())
      setShowNextWaveButton(false)

      setHero((h) => (h ? healHero(h) : h))

      // Start the next wave
      const spawnPos = level.path[0]
      const nodesCompleted = mapState?.visitedNodeIds.length ?? 0
      waveSpawnerRef.current = startWave(level.waves[nextWaveIndex], spawnPos, getProgressionLevel(), nodesCompleted)
      playNewWaveSound()
    },
    [currentWaveIndex, level, stats.money, hero, waveStartTime, getProgressionLevel, mapState],
  )

  // Set victory state with interest applied (for end of level)
  const setVictoryWithInterest = useCallback(() => {
    // Calculate interest with hero bonus (Dwarven Treasury)
    const interestBonus = hero ? getInterestBonus(hero) : 0
    const effectiveInterestRate = constants.INTEREST_RATE + interestBonus
    const interest = Math.floor(stats.money * effectiveInterestRate)

    setInterestAnimation(interest)
    setTimeout(() => setInterestAnimation(null), 2000)

    setStats((prev) => ({
      ...prev,
      money: prev.money + interest,
    }))

    // Clear summons, corpses, and soldiers when finishing a level
    setSummonedUnits([])
    setCorpses([])
    setBarracksSoldiers([])

    setGameState("victory")
  }, [hero, stats.money])

  // Start abduction animation when level is complete
  const startAbductionAnimation = useCallback(() => {
    if (!hero) return

    // Play level complete sound immediately when abduction starts
    playLevelCompleteSound()

    // Calculate enemy score (kills this level) and tower score
    const enemyScore = stats.score - scoreAtLevelStartRef.current
    const towerScore = Math.floor(calculateTowerScore(towers))
    const totalNewScore = enemyScore + towerScore

    setAbductionAnimation({
      phase: "camera_zoom",
      startTime: gameNow(),
      phaseStartTime: gameNow(),
      heroStartY: hero.position.y,
      scoreAtLevelStart: scoreAtLevelStartRef.current,
      scoreFinal: scoreAtLevelStartRef.current + totalNewScore,
      enemyScore,
      towerScore,
    })
  }, [hero, stats.score, towers])

  // Complete abduction animation and transition to victory
  const completeAbductionAnimation = useCallback(() => {
    // Add the tower bonus on top of the live score - the loop keeps running
    // during the animation, so kills earned meanwhile must not be discarded
    if (abductionAnimation) {
      setStats(prev => ({
        ...prev,
        score: Math.max(prev.score + abductionAnimation.towerScore, abductionAnimation.scoreFinal),
      }))
    }
    setAbductionAnimation(null)
    setIsLastWaveComplete(false)
    // Persist hero state BEFORE setting to null (preserves skill points, XP, etc.)
    if (hero) {
      persistedHeroRef.current = hero
    }
    setHero(null) // Remove hero from canvas so it doesn't reappear
    setVictoryWithInterest()
  }, [setVictoryWithInterest, abductionAnimation, hero])

  // Enter shop after last wave complete (called when player clicks "Enter Shop" button)
  // Note: This is now legacy - animation triggers automatically, but kept for compatibility
  const enterShop = useCallback(() => {
    if (isLastWaveComplete && !abductionAnimation) {
      startAbductionAnimation()
    }
  }, [isLastWaveComplete, abductionAnimation, startAbductionAnimation])

  const onNextLevelDropPodComplete = useCallback((rewards: DropPodRewards) => {
    pendingRewardsRef.current = rewards
    setGameState("landing")
  }, [])

  const startFirstWave = useCallback(() => {
    // Calculate interest with hero bonus (Dwarven Treasury) - same as other wave transitions
    const interestBonus = hero ? getInterestBonus(hero) : 0
    const effectiveInterestRate = constants.INTEREST_RATE + interestBonus
    const interest = Math.floor(stats.money * effectiveInterestRate)

    setInterestAnimation(interest)
    setTimeout(() => setInterestAnimation(null), 2000)

    setStats((prev) => ({
      ...prev,
      money: prev.money + interest,
    }))

    setShowNextWaveButton(false)
    setWaveStartTime(gameNow())

    // Get spawn position from the first path point
    const spawnPos = level.path[0]

    // Create the wave spawner with progression level and nodesCompleted
    const nodesCompleted = mapState?.visitedNodeIds.length ?? 0
    const newSpawner = startWave(level.waves[0], spawnPos, getProgressionLevel(), nodesCompleted)
    waveSpawnerRef.current = newSpawner
  }, [level, hero, stats.money, getProgressionLevel, mapState])

  const setDebugLevelIndex = useCallback((index: number) => {
    if (index >= 0 && index < levels.length) {
      setCurrentLevelIndex(index)
      // Set to last wave of that level
      const targetLevel = getLevelByIndex(index)
      if (targetLevel) {
        setCurrentWaveIndex(targetLevel.waves.length - 1)
      }
    }
  }, [])

  const restoreGameState = useCallback(
    (savedState: {
      gameState: GameState
      stats: GameStats
      towers: Tower[]
      hero: Hero | null
      selectedHeroType: HeroType
      currentLevelIndex: number
      currentWaveIndex: number
      showNextWaveButton: boolean
      freePremiumTowers: number
      landedPod: LandedPod | null
      summonedUnits: SummonedUnit[]
      heroInventory?: HeroInventory
      upgradeState?: UpgradeState
      mapState?: MapState | null
      heroismLevel?: number
    }) => {
      setGameState(savedState.gameState)
      setStats(savedState.stats)
      setTowers(savedState.towers)
      persistedTowersRef.current = savedState.towers
      setHero(savedState.hero)
      persistedHeroRef.current = savedState.hero
      selectedHeroTypeRef.current = savedState.selectedHeroType
      setCurrentLevelIndex(savedState.currentLevelIndex)
      setCurrentWaveIndex(savedState.currentWaveIndex)
      setShowNextWaveButton(savedState.showNextWaveButton)
      setFreePremiumTowers(savedState.freePremiumTowers)
      setLandedPod(savedState.landedPod)
      setSummonedUnits(savedState.summonedUnits)
      // Restore inventory/upgrades/map so a save-load roundtrip doesn't wipe
      // the player's items and progression (fields are optional in old saves)
      if (savedState.heroInventory) {
        setHeroInventory(savedState.heroInventory)
        persistedInventoryRef.current = savedState.heroInventory
      }
      if (savedState.upgradeState) {
        setUpgradeState(savedState.upgradeState)
        persistedUpgradesRef.current = savedState.upgradeState
      }
      if (savedState.mapState !== undefined) {
        setMapState(savedState.mapState)
      }
      if (savedState.heroismLevel !== undefined) {
        setHeroismLevel(savedState.heroismLevel)
      }
      persistedScoreRef.current = savedState.stats.score

      // Clear transient state
      setEnemies([])
      setProjectiles([])
      setSkillEffects([])
      setRootsEffects([])
      setFireEffects([])
      setSelectedTowerId(null)
      setSelectedEnemyId(null)
      setSelectedSummonId(null)
      setPendingSkillId(null)

      // Re-create wave spawner for the current wave
      waveSpawnerRef.current = createWaveSpawner()
    },
    [],
  )

  return {
    gameState,
    stats,
    enemies,
    towers,
    hero,
    projectiles,
    selectedTowerId,
    selectedHeroId,
    selectedEnemyId,
    level,
    waveSpawner: waveSpawnerRef.current,
    waveSpawnerRef,
    clearEnemiesRef,
    currentLevelIndex,
    currentWaveIndex,
    showNextWaveButton,
    waveStartTime,
    interestAnimation,
    sellAnimation,
    particleEffects,
    totalLevels: getTotalLevels(),
    freePremiumTowers,
    isScreenShaking,
    screenShakeOffset,
    selectedHeroType: selectedHeroTypeRef.current,
    landedPod,
    skillEffects,
    summonedUnits,
    selectedSummonId,
    pendingSkillId,
    pendingSkillExecution,
    rootsEffects,
    fireEffects,
    heroInventory,
    activeItemEffects,
    pendingItemSlot,
    corpses,
    aoeVisualEffects,
    pendingAoeWaves,
    lastUpgradedSkillId,
    droppedItems,
    laserBeamEffects,
    cloneRestoreEffect,
    isLastWaveComplete,
    abductionAnimation,
    mapState,
    isMapOpen,
    upgradeState,
    multiplayerState,
    barracksSoldiers,
    soldierSpawnPortraits,
    itemDropMessages,
    selectedSoldierId,
    isSettingRallyPoint,
    pendingRallyBarracksId,
    heroismLevel,
    actions: useMemo(() => ({
      startGame,
      selectHero,
      placeTower,
      upgradeTower: upgradeTowerById,
      sellTower,
      moveHero,
      pauseGame,
      resumeGame,
      pauseGameForSystem,
      resumeGameFromSystem,
      restartGame,
      restartWithCurrentHero,
      buyItem,
      sellItem,
      purchaseHeroism,
      purchaseSkillUpgrade,
      spendGold,
      setSelectedTowerId,
      setSelectedHeroId,
      selectEnemy,
      deselectEnemy,
      startNextWave,
      callWaveEarly,
      startNextLevel,
      removeParticleEffect,
      onDropPodComplete,
      onNextLevelDropPodComplete,
      onLandingComplete,
      startFirstWave,
      setDebugLevelIndex,
      useSkill: activateHeroSkill,
      useSkillByHotkey,
      upgradeHeroSkill,
      selectSummon,
      deselectSummon,
      selectSoldier,
      deselectSoldier,
      moveSummon,
      executeTargetedSkill,
      cancelPendingSkill,
      executeSkillAtPosition,
      setPendingSkillExecution,
      useItem,
      executeTargetedItem,
      cancelPendingItem,
      dropItemFromInventory,
      pickupDroppedItem,
      destroyDroppedItem,
      canPickupItem,
      restoreGameState,
      generateNewMap,
      advanceMapNode,
      selectMapNode,
      completeMapNode,
      revealEventNode,
      collectPelfReward,
      openMap,
      closeMap,
      openMapAfterLevel,
      startLevelByIndex,
      setVictoryWithInterest,
      enterShop,
      unlockUpgrade,
      startAbductionAnimation,
      completeAbductionAnimation,
       
    }), [
      startGame, selectHero, placeTower, upgradeTowerById, sellTower, moveHero,
      pauseGame, resumeGame, pauseGameForSystem, resumeGameFromSystem,
      restartGame, restartWithCurrentHero, buyItem, sellItem, purchaseHeroism, purchaseSkillUpgrade,
      spendGold, setSelectedTowerId, setSelectedHeroId, selectEnemy, deselectEnemy,
      startNextWave, callWaveEarly, startNextLevel, removeParticleEffect,
      onDropPodComplete, onNextLevelDropPodComplete, onLandingComplete,
      startFirstWave, setDebugLevelIndex, activateHeroSkill, useSkillByHotkey,
      upgradeHeroSkill, selectSummon, deselectSummon, selectSoldier,
      deselectSoldier, moveSummon, executeTargetedSkill, cancelPendingSkill,
      executeSkillAtPosition, setPendingSkillExecution, useItem,
      executeTargetedItem, cancelPendingItem, dropItemFromInventory,
      pickupDroppedItem, destroyDroppedItem, canPickupItem, restoreGameState,
      generateNewMap, advanceMapNode, selectMapNode, completeMapNode,
      revealEventNode, collectPelfReward, openMap, closeMap, openMapAfterLevel,
      startLevelByIndex, setVictoryWithInterest, enterShop, unlockUpgrade,
      startAbductionAnimation, completeAbductionAnimation,
    ]),
    // useState setters are stable - memoize once so the game loop's RAF effect
    // (which depends on this object) doesn't tear down and restart every render
    setters: useMemo(() => ({
      setEnemies,
      setTowers,
      setHero,
      setProjectiles,
      setStats,
      setGameState,
      setShowNextWaveButton,
      setWaveStartTime,
      setParticleEffects,
      setSkillEffects,
      setSummonedUnits,
      setRootsEffects,
      setFireEffects,
      setHeroInventory,
      setActiveItemEffects,
      setCorpses,
      setAoeVisualEffects,
      setPendingAoeWaves,
      setDroppedItems,
      setLaserBeamEffects,
      triggerCloneRestoreEffect,
      setIsLastWaveComplete,
      setHeroismLevel,
      setAbductionAnimation,
      setMapState,
      setIsMapOpen,
      setBarracksSoldiers,
      setSoldierSpawnPortraits,
      setItemDropMessages,
      setIsSettingRallyPoint,
      setPendingRallyBarracksId,
    }), [triggerCloneRestoreEffect]),
  }
}
